import type {
  Incentive,
  PlacementOption,
  TransportationOption,
  TransportationSelection
} from "../types.js";

/**
 * Lógica PURA da Etapa 3 (Confirmação de envio): seleção da transportadora
 * parceira da Amazon e o "Total estimado". Sem React/IO — testável isoladamente.
 *
 * Regra SDD §9.5: no MVP só são selecionáveis as opções
 * `AMAZON_PARTNERED_CARRIER` (SPD). As `USE_YOUR_OWN_CARRIER` ficam de fora
 * (exigem override Admin) e, em produção, vêm às centenas sem cotação — exibi-las
 * misturadas é o que tornava a Etapa 3 inútil. O "Total estimado" = taxa de
 * posicionamento (placement, líquida de descontos) + cotação da transportadora.
 */
export const PARTNERED_SOLUTION = "AMAZON_PARTNERED_CARRIER";
export const OWN_CARRIER_SOLUTION = "USE_YOUR_OWN_CARRIER";
/** Modo de transporte SPD do MVP (§9.5) — único selecionável no fluxo padrão. */
export const GROUND_SMALL_PARCEL_MODE = "GROUND_SMALL_PARCEL";

/** Opções de transportadora PARCEIRA da Amazon — as únicas selecionáveis (§9.5). */
export function partneredOptions(
  options: readonly TransportationOption[]
): TransportationOption[] {
  return options.filter((o) => o.shippingSolution === PARTNERED_SOLUTION);
}

/** Quantidade de opções "transportadora própria" (informativo). */
export function ownCarrierCount(
  options: readonly TransportationOption[]
): number {
  return options.filter((o) => o.shippingSolution === OWN_CARRIER_SOLUTION)
    .length;
}

/** A opção é uma TRANSPORTADORA PRÓPRIA (USE_YOUR_OWN_CARRIER)? */
export function isOwnCarrier(option: TransportationOption): boolean {
  return option.shippingSolution === OWN_CARRIER_SOLUTION;
}

/** A opção é uma TRANSPORTADORA PARCEIRA da Amazon (AMAZON_PARTNERED_CARRIER)? */
export function isPartnered(option: TransportationOption): boolean {
  return option.shippingSolution === PARTNERED_SOLUTION;
}

/** Nome legível da transportadora (nome > código > rótulo genérico). */
export function carrierDisplayName(option: TransportationOption): string {
  const name = option.carrier.name;
  if (name !== null && name.trim().length > 0) {
    return name;
  }
  const code = option.carrier.alphaCode;
  if (code !== null && code.trim().length > 0) {
    return code;
  }
  return "Transportadora";
}

/**
 * Transportadoras próprias RECONHECÍVEIS para o operador escolher quando a Amazon
 * não ofereceu parceira (ou para casos como Hazmat). A `listTransportationOptions`
 * devolve centenas de `USE_YOUR_OWN_CARRIER`, a maioria com código críptico de 4-5
 * letras (GUBB, FSNH…) inútil ao usuário. Mostramos só as de NOME real — grandes
 * transportadoras do Reino Unido (DPD, UPS, Parcelforce, DHL, Yodel…) e quaisquer
 * com nome de empresa (com espaço) — para a escolha não virar uma lista ilegível.
 */
const KNOWN_OWN_CARRIER_PRIORITY: readonly string[] = [
  "DPD",
  "UPS",
  "UNITED PARCEL",
  "PARCELFORCE",
  "PARCEL FORCE",
  "DHL",
  "YODEL",
  "FEDEX",
  "TNT",
  "ROYAL MAIL",
  "EVRI",
  "HERMES",
  "WHISTL",
  "UK MAIL"
];

function ownCarrierPriority(option: TransportationOption): number {
  const upper = carrierDisplayName(option).toUpperCase();
  const index = KNOWN_OWN_CARRIER_PRIORITY.findIndex((token) =>
    upper.includes(token)
  );
  return index === -1 ? KNOWN_OWN_CARRIER_PRIORITY.length : index;
}

/** A transportadora própria tem NOME reconhecível (grande transportadora ou empresa)? */
function isRecognizableOwnCarrier(option: TransportationOption): boolean {
  const name = option.carrier.name;
  if (name === null || name.trim().length === 0) {
    return false;
  }
  if (ownCarrierPriority(option) < KNOWN_OWN_CARRIER_PRIORITY.length) {
    return true;
  }
  // Nome de empresa multi-palavra (ex.: "DHL AIRWAYS INC") — distingue dos códigos
  // crípticos de uma palavra. "Other" (genérico) fica de fora.
  return name.trim().includes(" ") && name.trim().toUpperCase() !== "OTHER";
}

/**
 * Opções de TRANSPORTADORA PRÓPRIA selecionáveis: SPD (GROUND_SMALL_PARCEL), com
 * nome reconhecível, deduplicadas por carrier (uma entrada por transportadora) e
 * ordenadas com as grandes (DPD, UPS, Parcelforce…) no topo. É o caminho para o
 * operador avançar quando a Amazon não ofereceu parceira ou para Hazmat.
 */
export function selectableOwnCarrierOptions(
  options: readonly TransportationOption[]
): TransportationOption[] {
  const byCarrier = new Map<string, TransportationOption>();
  for (const option of options) {
    if (!isOwnCarrier(option)) continue;
    if (option.shippingMode !== GROUND_SMALL_PARCEL_MODE) continue;
    if (!isRecognizableOwnCarrier(option)) continue;
    const key = carrierKey(option);
    const existing = byCarrier.get(key);
    // Dedupe por carrier: mantém a de menor cotação (own-carrier costuma vir sem
    // cotação, então normalmente a primeira encontrada).
    if (existing === undefined) {
      byCarrier.set(key, option);
      continue;
    }
    const cost = option.quote?.cost?.amount ?? Number.POSITIVE_INFINITY;
    const existingCost =
      existing.quote?.cost?.amount ?? Number.POSITIVE_INFINITY;
    if (cost < existingCost) {
      byCarrier.set(key, option);
    }
  }
  return [...byCarrier.values()].sort((a, b) => {
    const pa = ownCarrierPriority(a);
    const pb = ownCarrierPriority(b);
    if (pa !== pb) return pa - pb;
    return carrierDisplayName(a).localeCompare(carrierDisplayName(b));
  });
}

/** Soma os valores monetários de incentivos (mesma moeda assumida). */
export function sumIncentiveAmounts(items: readonly Incentive[]): number {
  return items.reduce((total, i) => total + (i.value?.amount ?? 0), 0);
}

/** Custo líquido de posicionamento (fees − discounts) da placement option. */
export function placementNetCost(option: PlacementOption | null): number {
  if (option === null) {
    return 0;
  }
  return sumIncentiveAmounts(option.fees) - sumIncentiveAmounts(option.discounts);
}

/** Taxa de posicionamento BRUTA (só `fees`) — o que sai do bolso neste envio. */
export function placementFeeAmount(option: PlacementOption | null): number {
  return option === null ? 0 : sumIncentiveAmounts(option.fees);
}

/** Desconto oferecido pela Amazon nesta opção (só `discounts`). */
export function placementDiscountAmount(option: PlacementOption | null): number {
  return option === null ? 0 : sumIncentiveAmounts(option.discounts);
}

/**
 * Opção de posicionamento RECOMENDADA. O schema oficial v2024-03-20 NÃO tem
 * campo "recomendada" (`Incentive` com `type: DISCOUNT` é o único sinal), então a
 * recomendação é DERIVADA: menor custo líquido (fees − discounts); em empate,
 * a que traz maior desconto da Amazon; persistindo o empate, a ordem da Amazon.
 * Sem isto o wizard pré-selecionava `placementOptions[0]` — a ordem crua da
 * SP-API, que não tem relação com custo e podia fixar a opção mais cara.
 */
export function recommendedPlacement(
  options: readonly PlacementOption[]
): PlacementOption | null {
  let best: PlacementOption | null = null;
  for (const option of options) {
    if (best === null) {
      best = option;
      continue;
    }
    const diff = placementNetCost(option) - placementNetCost(best);
    if (diff < 0) {
      best = option;
      continue;
    }
    if (
      diff === 0 &&
      placementDiscountAmount(option) > placementDiscountAmount(best)
    ) {
      best = option;
    }
  }
  return best;
}

export interface CostBreakdown {
  /**
   * Taxa de posicionamento BRUTA (`fees`). NÃO desconta `discounts`: o desconto
   * que a Amazon oferece nestas opções costuma ter `target: "Fulfillment Fee
   * Discount"` — abate a tarifa de fulfillment cobrada DEPOIS, por unidade
   * vendida, e não o custo deste envio. Abatê-lo aqui produzia um "Total
   * estimado" NEGATIVO (ex.: £0 de taxa − £12 de desconto + £2,86 de frete =
   * −£9,14) e mentia sobre o desembolso do envio.
   */
  placement: number;
  /** Desconto Amazon da opção escolhida — exibido à parte, como benefício. */
  placementDiscount: number;
  /** Custo de transporte da opção selecionada; null se sem cotação/seleção. */
  transport: number | null;
  /** placement + transport; null quando não há cotação de transporte. */
  total: number | null;
  currency: string;
}

/**
 * Quebra o "Total estimado" em posicionamento + transporte. `selected` pode ser
 * UMA opção (single-shipment) ou a LISTA de opções escolhidas (uma por shipment,
 * vinda de {@link buildCarrierCoverageSelections}); o transporte é a SOMA das
 * cotações de todas elas — num plano dividido, somar só um shipment subestimaria
 * o total mostrado antes da confirmação (invariante: exibir custos reais antes
 * de decisões irreversíveis).
 */
export function computeBreakdown(
  placement: PlacementOption | null,
  selected: TransportationOption | TransportationOption[] | null
): CostBreakdown {
  const placementCost = placementFeeAmount(placement);
  const placementDiscount = placementDiscountAmount(placement);
  const selectedList =
    selected === null ? [] : Array.isArray(selected) ? selected : [selected];

  let transport: number | null = null;
  let transportCurrency: string | null = null;
  for (const option of selectedList) {
    const cost = option.quote?.cost?.amount;
    if (cost == null) continue;
    transport = (transport ?? 0) + cost;
    transportCurrency =
      transportCurrency ?? option.quote?.cost?.currencyCode ?? null;
  }

  const currency =
    transportCurrency ??
    placement?.fees[0]?.value?.currencyCode ??
    placement?.discounts[0]?.value?.currencyCode ??
    "GBP";
  return {
    placement: placementCost,
    placementDiscount,
    transport,
    total: transport === null ? null : placementCost + transport,
    currency
  };
}

/**
 * Opção parceira mais barata (menor cotação) — sugestão de pré-seleção. Entre as
 * cotadas escolhe a de menor custo; se nenhuma tem cotação, devolve a primeira
 * parceira (ou null se não houver parceira).
 */
export function cheapestPartnered(
  options: readonly TransportationOption[]
): TransportationOption | null {
  const partnered = partneredOptions(options);
  let best: TransportationOption | null = null;
  let bestCost = Number.POSITIVE_INFINITY;
  for (const option of partnered) {
    const cost = option.quote?.cost?.amount;
    if (cost !== undefined && cost < bestCost) {
      best = option;
      bestCost = cost;
    }
  }
  return best ?? partnered[0] ?? null;
}

/**
 * Chave de identidade da transportadora — IDÊNTICA à do backend
 * (packages/sp-api/src/transportation-options.ts `carrierKey`): `alphaCode ??
 * name ?? ""`. A regra §9.5 (validateTransportationSelection) bloqueia carriers
 * misturados comparando exatamente esta chave; divergir aqui faria "mesmo
 * carrier" discordar entre o cliente e o servidor.
 */
export function carrierKey(option: TransportationOption): string {
  return option.carrier.alphaCode ?? option.carrier.name ?? "";
}

/**
 * A opção é SPD (GROUND_SMALL_PARCEL) da MESMA solução de envio escolhida —
 * parceira OU própria. Generaliza o antigo `isPartneredSpd`: a cobertura passou a
 * aceitar transportadora PRÓPRIA (Hazmat / quando não há parceira), então o casamento
 * por shipment respeita a solução da opção que o operador marcou (não força parceira).
 */
function matchesSelectedSpd(
  option: TransportationOption,
  solution: string
): boolean {
  return (
    option.shippingSolution === solution &&
    option.shippingMode === GROUND_SMALL_PARCEL_MODE
  );
}

/** shipmentIds distintos (ordem de 1ª aparição) entre TODAS as opções. */
function distinctShipmentIds(
  options: readonly TransportationOption[]
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const option of options) {
    if (!seen.has(option.shipmentId)) {
      seen.add(option.shipmentId);
      ids.push(option.shipmentId);
    }
  }
  return ids;
}

/** Opção SPD mais barata do `key` (mesma solução) para `shipmentId` (null se nenhuma). */
function cheapestMatchForShipment(
  options: readonly TransportationOption[],
  shipmentId: string,
  key: string,
  solution: string
): TransportationOption | null {
  let best: TransportationOption | null = null;
  let bestCost = Number.POSITIVE_INFINITY;
  for (const option of options) {
    if (option.shipmentId !== shipmentId) continue;
    if (!matchesSelectedSpd(option, solution)) continue;
    if (carrierKey(option) !== key) continue;
    const cost = option.quote?.cost?.amount ?? Number.POSITIVE_INFINITY;
    if (best === null || cost < bestCost) {
      best = option;
      bestCost = cost;
    }
  }
  return best;
}

export interface CarrierCoverageOk {
  ok: true;
  carrierKey: string;
  /** UMA seleção por shipment (cobertura total) — payload de selectTransportation. */
  selections: TransportationSelection[];
  /** Opção escolhida por shipment — para somar o custo de transporte real. */
  options: TransportationOption[];
}

export interface CarrierCoverageError {
  ok: false;
  carrierKey: string;
  /** Envios sem opção partnered+SPD do carrier escolhido (cobertura incompleta). */
  uncoveredShipmentIds: string[];
}

export type CarrierCoverageResult = CarrierCoverageOk | CarrierCoverageError;

/**
 * Monta a seleção de transporte de COBERTURA TOTAL exigida pela regra §9.5
 * (backend `validateTransportationSelection`): cada shipment presente nas opções
 * coberto exatamente uma vez, todos com o MESMO carrier escolhido. Enviar só a
 * seleção de um shipment num plano com placement dividido retorna
 * TRANSPORTATION_INCOMPLETE_COVERAGE → applyTransportationOptionSelected vira
 * no-op silencioso → a UI fica presa no poll de confirmação.
 *
 * Para cada `shipmentId` em `options` (incluindo os que só têm opção de
 * transportadora própria), escolhe a opção AMAZON_PARTNERED_CARRIER +
 * GROUND_SMALL_PARCEL do carrier de `selectedCarrier`. No shipment do próprio
 * `selectedCarrier`, usa exatamente a opção que o usuário marcou; nos demais, a
 * opção do mesmo carrier (mais barata em caso de empate). Se algum shipment não
 * tiver opção do carrier escolhido, devolve erro de cobertura (em vez de enviar
 * uma seleção parcial e travar no no-op).
 */
export function buildCarrierCoverageSelections(
  selectedCarrier: TransportationOption,
  options: readonly TransportationOption[]
): CarrierCoverageResult {
  const key = carrierKey(selectedCarrier);
  const solution = selectedCarrier.shippingSolution;
  const selections: TransportationSelection[] = [];
  const chosen: TransportationOption[] = [];
  const uncovered: string[] = [];

  for (const shipmentId of distinctShipmentIds(options)) {
    const option =
      shipmentId === selectedCarrier.shipmentId &&
      matchesSelectedSpd(selectedCarrier, solution)
        ? selectedCarrier
        : cheapestMatchForShipment(options, shipmentId, key, solution);
    if (option === null) {
      uncovered.push(shipmentId);
      continue;
    }
    selections.push({
      shipmentId,
      transportationOptionId: option.transportationOptionId
    });
    chosen.push(option);
  }

  if (uncovered.length > 0) {
    return { ok: false, carrierKey: key, uncoveredShipmentIds: uncovered };
  }
  return { ok: true, carrierKey: key, selections, options: chosen };
}

/** Status terminais do workflow — não há como confirmar o envio (§6.3). */
export const TERMINAL_WORKFLOW_STATUSES = new Set<string>([
  "FAILED",
  "TIMED_OUT",
  "CANCELLED"
]);

/** Estágios já APÓS a confirmação do transporte (Etapa 3 concluída). */
export const POST_TRANSPORT_STAGES = new Set<string>([
  "CONFIRM_PLACEMENT_OPTION",
  "GENERATE_DELIVERY_WINDOW_OPTIONS",
  "LIST_DELIVERY_WINDOW_OPTIONS",
  "CONFIRM_DELIVERY_WINDOWS",
  "CONFIRM_TRANSPORTATION",
  "FINALIZE_WORKFLOW"
]);

/** Estágios em que a Amazon está gerando/listando as opções de transporte. */
export const GENERATING_TRANSPORT_STAGES = new Set<string>([
  "GENERATE_TRANSPORTATION_OPTIONS",
  "LIST_TRANSPORTATION_OPTIONS"
]);

/**
 * Decisão PURA da Etapa 3 a partir do estado real do plano. O ponto crítico é o
 * `"preparing"`: quando o operador clica "Avançar" para a Etapa 3 logo após o
 * Passo 2, o plano ainda está progredindo pelos estágios de backend até a
 * placement (SET_PACKING_INFORMATION → GENERATE/LIST_PLACEMENT_OPTIONS) — que
 * podem levar minutos. O comportamento ANTERIOR caía no `default` e mostrava um
 * erro fixo ("Conclua o Passo 2") UMA vez, sem re-pollar: o plano ficava pronto
 * mas a tela travava ("espera eternamente"). `"preparing"` sinaliza ao chamador
 * para POLLAR até a placement/transporte/terminal, em vez de dar erro.
 */
export type Step3Decision =
  | "terminal"
  | "confirmed"
  | "delivery-window"
  | "transport-ready"
  | "generating"
  | "need-placement"
  | "no-placement"
  | "preparing";

export function decideStep3Phase(input: {
  workflowStatus: string;
  workflowStage: string | null | undefined;
  transportCount: number;
  placementCount: number;
  /**
   * O backend ainda está PROCURANDO transportadora parceira (sondando outra
   * opção de posicionamento). Enquanto for true, as opções já publicadas são
   * PARCIAIS: concluir agora anunciaria "sem parceira" havendo parceira a
   * caminho — foi exatamente o defeito do plano 853c2022 (a tela congelou 17s
   * antes de as parceiras DPD chegarem ao store).
   */
  partneredSearching?: boolean;
}): Step3Decision {
  const stage = input.workflowStage ?? "";
  if (TERMINAL_WORKFLOW_STATUSES.has(input.workflowStatus)) {
    return "terminal";
  }
  if (input.workflowStatus === "COMPLETED" || POST_TRANSPORT_STAGES.has(stage)) {
    return "confirmed";
  }
  if (stage === "WAIT_FOR_DELIVERY_WINDOWS") {
    return "delivery-window";
  }
  // Busca em andamento vence qualquer lista já publicada (ver acima).
  if (input.partneredSearching === true) {
    return "generating";
  }
  if (input.transportCount > 0 && stage === "WAIT_FOR_TRANSPORTATION_OPTIONS") {
    return "transport-ready";
  }
  if (GENERATING_TRANSPORT_STAGES.has(stage)) {
    return "generating";
  }
  if (stage === "WAIT_FOR_PLACEMENT_OPTION") {
    return input.placementCount > 0 ? "need-placement" : "no-placement";
  }
  // Qualquer outro estágio não-terminal = plano ainda progredindo até a
  // placement (ou estágios iniciais do Passo 2): pollar, não dar erro.
  return "preparing";
}

/**
 * Decisão PURA de cada tick do poll pós-placement ("Buscar transportadoras").
 * O ponto crítico: o store de transporte fica NÃO-VAZIO já com a lista da
 * PRIMEIRA placement enquanto o workflow ainda está sondando as demais em busca
 * de transportadora parceira (placement-partnered-probe) — concluir "pronto"
 * pela lista não-vazia congela a tela na lista PROVISÓRIA (own-carrier) e
 * anuncia "sem parceira" quando a parceira chega segundos depois. Só o estágio
 * `WAIT_FOR_TRANSPORTATION_OPTIONS` é o sinal autoritativo de que a busca
 * terminou (mesma regra do `decideStep3Phase`).
 */
export type TransportPollDecision =
  | "keep-polling"
  | "transport-ready"
  | "delivery-window"
  | "confirmed"
  | "terminal";

export function decideTransportPoll(input: {
  workflowStatus: string;
  workflowStage: string | null | undefined;
  transportCount: number;
  /**
   * Sinal EXPLÍCITO do backend (`partneredSearch.searching`) de que a sonda de
   * placements ainda está rodando. Independe de timing e cobre a janela em que
   * o estágio já mudou mas a busca não terminou.
   */
  partneredSearching?: boolean;
}): TransportPollDecision {
  const stage = input.workflowStage ?? "";
  if (TERMINAL_WORKFLOW_STATUSES.has(input.workflowStatus)) {
    return "terminal";
  }
  if (input.workflowStatus === "COMPLETED" || POST_TRANSPORT_STAGES.has(stage)) {
    return "confirmed";
  }
  if (stage === "WAIT_FOR_DELIVERY_WINDOWS") {
    return "delivery-window";
  }
  if (input.partneredSearching === true) {
    return "keep-polling";
  }
  if (stage === "WAIT_FOR_TRANSPORTATION_OPTIONS" && input.transportCount > 0) {
    return "transport-ready";
  }
  // Ainda gerando/sondando placements (ou aguardando o LIST gravar o store):
  // continuar pollando até o workflow parar num estágio de espera/terminal.
  return "keep-polling";
}

/** Rótulo do modo de envio (SP-API) em pt-BR. */
export function shippingModeLabel(mode: string): string {
  switch (mode) {
    case "GROUND_SMALL_PARCEL":
      return "Encomenda terrestre (SPD)";
    case "FREIGHT_LTL":
      return "Carga fracionada (LTL)";
    case "FREIGHT_FTL_PALLET":
    case "FREIGHT_FTL_NONPALLET":
      return "Carga completa (FTL)";
    default:
      return mode;
  }
}

export function formatMoney(amount: number, currency: string): string {
  return amount.toLocaleString("pt-BR", { style: "currency", currency });
}
